import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { MenuItem } from 'primeng/api';
import { RedirectUrls } from './models/redirect-urls.enum';
import { UserDetails } from 'src/app/shared/models/user-details.model';
import { UserHelper } from './models/user-helper.model';
import { UserRights } from './models/user-rights.model';
import { SettingFeatureFlags } from './models/setting-feature-flags.model';
import { SettingConfigurations } from './models/setting-configurations.model';
import { CookieStateService } from 'src/app/shared/cookie/cookie-state-service';
import { interval } from 'rxjs';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { AuthConfigService, TranslationService } from 'projects/dop-core/src/public-api';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class DopMenuService {

  constructor(
    private oauthService: OAuthService,
    private httpClient: HttpClient,
    private appConfigService: AppConfigService,
    private cookieStateService: CookieStateService,
    private authConfigService: AuthConfigService) {
  }

  private featureFlags: SettingFeatureFlags;
  private configurations: SettingConfigurations;

  loadMenuItems(
    userDetails: UserDetails, displayName: string, featureFlags: SettingFeatureFlags,
    configurations: SettingConfigurations, baseUrl: string): MenuItem[] {
    this.featureFlags = featureFlags;
    this.configurations = configurations;

    const userHelper = new UserHelper(userDetails);
    const menuItems: MenuItem[] = [];
    const userRights = new UserRights(userDetails, userHelper, this.configurations);
    
    // left nav changes container
    // Added class p-menuitem-current
    // end

    if (this.configurations.enableOSIS && userRights.canOnlyPlaceOrderSubmissionForm) {
      const orderingSubMenuItems = this.getLimitedOrderingSubMenuItems(baseUrl, userRights);

      // Ordering Menu
      menuItems.push({
        label: TranslationService.translate('vbeiOrderingServices'),
        icon: 'ei ei-cart ei-26',
        items: orderingSubMenuItems,
        styleClass: orderingSubMenuItems.length > 8 ? 'p-menuitem-current two-column' : 'p-menuitem-current'

      });

      // Lab contacts Menu
      this.addLabContactsMenuItems(baseUrl, userRights, menuItems);

      // Support Menu
      menuItems.push({
        label: TranslationService.translate('vbeiSupport'),
        icon: 'ei ei-bubble-question ei-26',
        url: this.configurations.enableAdvancedSupportPage ? baseUrl + RedirectUrls.advanceSupport : baseUrl + RedirectUrls.supportUrl
      });

      // User Menu
      menuItems.push({
        label: displayName,
        icon: 'ei ei-user ei-26',
        items: this.getUserLoginSubMenuItems(baseUrl),
        styleClass: ''
      });

    } else {

      const orderSubMenuItems: MenuItem[] = [];

      if (userRights.canAccessOrderingService || userRights.canPlaceOrderSubmissionForm ||
        userRights.canOnlyPlaceOrderSubmissionForm || userRights.canPerformSampling || userRights.canPlaceGuidedOrder) {
        const subMenus = this.getOrderingSubMenuItems(baseUrl, userRights);
        orderSubMenuItems.push(...subMenus);
      }

      menuItems.push({
        label: TranslationService.translate('vbeiOrderingServices'),
        icon: 'ei ei-cart ei-26',
        items: orderSubMenuItems,
        styleClass: orderSubMenuItems.length > 8 ? 'p-menuitem-current two-column' : 'p-menuitem-current'
      });

      // Results Menu
      if (userRights.canAccessResults && (userRights.canAccessSelfReport || userRights.canAccessReports
        || userRights.canAccessResultsByOrder || userRights.canAccessResultsConfiguration)) {
        const resultsSubMenuItems = this.getResultsSubMenuItems(baseUrl, userRights);

        menuItems.push({
          label: TranslationService.translate('vbeiResults'),
          icon: 'ei ei-clipboard-text ei-26',
          items: resultsSubMenuItems,
          styleClass: resultsSubMenuItems.length > 8 ? 'two-column' : ''

        });
      }

      // Results(new) Menu
      if ((!userRights.isCustomerUser && this.configurations.useResults) || userRights.canAccessNewResults) {
        const resultsNewSubMenuItems = this.getNewResultsSubMenuItems(this.configurations.myResultsNewUrl);

        menuItems.push({
          label: TranslationService.translate('vbeiResults'),
          icon: 'ei ei-clipboard-text ei-26',
          items: resultsNewSubMenuItems,
          styleClass: resultsNewSubMenuItems.length > 8 ? 'new-tag two-column' : 'new-tag'

        });
      }

      // Documents Menu
      if (!(userRights.isCustomerUser && userRights.canOnlyPlaceOrderSubmissionForm)
        && (userRights.canAccessDocuments || (userRights.canViewInvoices && !this.configurations.disableInvoices))) {
        const documentsSubMenuItems = this.getDocumentsSubMenuItems(baseUrl, userRights);

        menuItems.push({
          label: TranslationService.translate('vbeiDocuments'),
          icon: 'ei ei-folder ei-26',
          items: documentsSubMenuItems,
          styleClass: documentsSubMenuItems.length > 8 ? 'two-column' : ''

        });
      }

      // Drop-off locations Menu
      // Commenting as it was mentioned not required as part of PBI:2147416
      // if (this.configurations.enableDropOff) {
      //   menuItems.push({
      //     label: 'Drop-off locations',
      //     icon: 'ei ei-map ei-26',
      //     url: baseUrl + RedirectUrls.dropOffLocationsUrl
      //   });
      // }

      // TODO: Local App Menu Missing, check what it is

      // Accounts Menu
      if (userRights.canAccessAccountsManagement) {
        const accountMenuItems = this.getAccountSubMenuItems(baseUrl, userRights);
        if (accountMenuItems.some) {
          menuItems.push({
            label: TranslationService.translate('eiAccounts'),
            icon: 'ei ei-users2 ei-26',
            items: accountMenuItems,
            styleClass: accountMenuItems.length > 8 ? 'two-column' : ''

          });
        }
      }

      // Settings Menu
      if (userRights.canAccessResultsConfiguration) {
        const settingsSubMenuItems = this.getSettingsSubMenuItems(baseUrl, userRights);

        menuItems.push({
          label: TranslationService.translate('eiSettings'),
          icon: 'ei ei-cog ei-26',
          items: settingsSubMenuItems,
          styleClass: settingsSubMenuItems.length > 8 ? 'two-column' : ''
        });
      }

      // Alerts Menu
      if (this.configurations.enableAlerts && (!userRights.isCustomerUser || userRights.canManageAlerts)) {
        const alertsSubMenuItems = this.getAlertsSubMenuItems(baseUrl);

        menuItems.push({
          label: TranslationService.translate('vbeiAlertsTitle'),
          icon: 'ei ei-alarm ei-26',
          items: alertsSubMenuItems,
          styleClass: alertsSubMenuItems.length > 8 ? 'two-column' : ''
        });
      }

      // Assessment admin Menu
      if (this.configurations.enableMultipleAssessments && userRights.canManageAssessment) {
        const assessmentsSubMenuItems = this.getAssessmentSubMenuItems(baseUrl);

        menuItems.push({
          label: TranslationService.translate('vbeiAssessmentAdmin'),
          icon: 'ei ei-cog ei-26',
          items: assessmentsSubMenuItems,
          styleClass: assessmentsSubMenuItems.length > 8 ? 'two-column' : ''
        });
      }

      // TODO: OSO Menu Missing
      // TODO: Logistic Menu Missing

      menuItems.push({ separator: true });

      const impersonatedData = this.cookieStateService.getState();
      if (userRights.canAccessImpersonation && !impersonatedData?.isImpersonated) {
        menuItems.push({
          label: TranslationService.translate('vbeiImpersonate'),
          icon: 'ei ei-theater ei-26',
          url: baseUrl + 'Impersonate'
        });
      }

      if (impersonatedData && impersonatedData.isImpersonated) {
        menuItems.push({
          label: TranslationService.translate('vbeiEimasStopImpersonate'),
          icon: 'ei ei-theater ei-26',
          url: baseUrl + 'stopimpersonate'
        });
      }

      if (userRights.isCustomerUser && this.configurations.enableContactPage) {
        // TODO: Contact menu
      }

      if (this.configurations.enableProductManagement && userRights.canPlaceOrder && userRights.canManageAndPlaceOrderForProducts) {
        // TODO: Product Management Menu missing
      }

      // Lab contacts Menu
      this.addLabContactsMenuItems(baseUrl, userRights, menuItems);

      // Support Menu
      menuItems.push({
        label: TranslationService.translate('vbeiSupport'),
        icon: 'ei ei-bubble-question ei-26',
        url: this.configurations.enableAdvancedSupportPage ? baseUrl + RedirectUrls.advanceSupport : baseUrl + RedirectUrls.supportUrl
      });

      // User Menu
      menuItems.push({
        label: displayName,
        icon: 'ei ei-user-3 ei-26',
        items: this.getUserLoginSubMenuItems(baseUrl),
        styleClass: 'user-menu'
      });
    }
    return menuItems;
  }

  getOrderingSubMenuItems(baseUrl: string, userRights: UserRights): MenuItem[] {
    const orderSubMenuItems: MenuItem[] = [];

    if (userRights.canAccessOrderingService) {
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiCreateNewOrder'),
        icon: 'ei ei-cart-plus ei-26',
        url: baseUrl + RedirectUrls.createNewOrderUrl,
        styleClass: 'parent'
      });

      if (this.configurations.enableExpressOrdering && userRights.canExpressOrdering) {
        orderSubMenuItems.push({
          label: TranslationService.translate('vbeiExpressOrdering'),
          icon: 'ei ei-cart-express ei-26',
          url: this.configurations.expressOrderingUrl,
          styleClass: 'parent'
        });
      }

      if (!this.configurations.newWaysOfCreatingOrdersEnabled) {
        orderSubMenuItems.push({
          label: TranslationService.translate('vbeiViewFavouriteOrders'),
          icon: 'ei ei-star ei-26',
          url: baseUrl + RedirectUrls.orderFromFavouritesUrl,
          styleClass: 'parent'
        });
      }
    }

    if (userRights.canPlaceGuidedOrder) {
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiEFCTOrdering'),
        icon: 'ei ei-cart-full ei-26',
        url: this.oauthService.redirectUri,
        styleClass: 'parent ordering-new'
      });
    }

    if (this.configurations.enableOSIS && (userRights.canPlaceOrderSubmissionForm || userRights.canOnlyPlaceOrderSubmissionForm)) {
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiCreateSubmissionForm'),
        icon: 'ei ei-file-empty ei-26',
        url: baseUrl + RedirectUrls.downloadSubmissionFormUrl,
        styleClass: 'parent'
      });
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiSubmissionForm'),
        icon: 'ei ei-file-check ei-26',
        url: baseUrl + RedirectUrls.uploadSubmissionFormUrl,
        styleClass: 'parent'
      });
    }

    if (userRights.canAccessOrderingService || userRights.canPerformSampling) {
      if(this.configurations.usePartialOrderEntry && userRights.canAccessOrderingService){
        orderSubMenuItems.push({
          label: TranslationService.translate('vbeiImportOrder'),
          icon: 'ei ei-upload2 ei-26',
          url: baseUrl + RedirectUrls.importOrderUrl,
          styleClass: 'parent'
        });
      }
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiViewPendingOrders'),
        icon: 'ei ei-cart ei-26',
        url: baseUrl + RedirectUrls.pendingOrdersUrl,
        styleClass: 'parent'
      });
    }

    orderSubMenuItems.push({
      label: TranslationService.translate('vbeiViewValidatedOrders'),
      icon: 'ei ei-cart-full ei-26',
      url: baseUrl + RedirectUrls.validatedOrdersUrl,
      styleClass: 'parent'
    });

    if (userRights.canAccessOrderingService) {
      if (!this.configurations.disableViewQuotation) {
        orderSubMenuItems.push({
          label: TranslationService.translate('vbeiViewQuotations'),
          icon: 'ei ei-tags ei-26',
          url: baseUrl + RedirectUrls.viewQuotationsUrl,
          styleClass: 'parent'
        });
      }
      if ((!userRights.isCustomerUser && userRights.canAccessImpersonation) || userRights.isCustomerUser) {
        if (this.configurations.useOfferFileExport) {
          orderSubMenuItems.push({
            label: TranslationService.translate('vbeiExportOffer'),
            icon: 'ei ei-download2 ei-26',
            url: baseUrl + RedirectUrls.exportOfferUrl,
            styleClass: 'parent'
          });
        }
      }

      if (this.configurations.enableProject) {
        orderSubMenuItems.push({
          label: TranslationService.translate('vbeiManageProjects'),
          icon: 'ei ei-library ei-26',
          url: baseUrl + RedirectUrls.manageProjectsUrl,
          styleClass: 'parent'
        });
      }
      if (this.configurations.enableSampleTaker) {
        orderSubMenuItems.push({
          label: TranslationService.translate('vbeiManageSampleTakers'),
          icon: 'ei ei-users ei-26',
          url: baseUrl + RedirectUrls.manageSampleTakerUrl,
          styleClass: 'parent'
        });
      }
    }

    this.addManageFavoriteSampleSubMenu(baseUrl, userRights, orderSubMenuItems);

    if (this.featureFlags.manageOrderingConfigurations && userRights.canManageOrderingConfigurations) {
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiManageOrderingConfigurations'),
        icon: 'ei ei-cog ei-26',
        url: baseUrl + RedirectUrls.manageOrderingConfigurationsUrl,
        styleClass: 'parent'
      });
    }

    if (userRights.canAccessOrderingService && this.configurations.manageFavouriteOrders) {
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiManageFavouriteOrders'),
        icon: 'ei ei-star ei-26',
        url: baseUrl + RedirectUrls.manageFavouriteOrdersUrl,
        styleClass: 'parent'
      });
    }

    return orderSubMenuItems;
  }

  getLimitedOrderingSubMenuItems(baseUrl: string, userRights: UserRights): MenuItem[] {
    const orderSubMenuItems: MenuItem[] = [];

    orderSubMenuItems.push({
      label: TranslationService.translate('vbeiCreateSubmissionForm'),
      icon: 'ei ei-file-empty ei-26',
      url: baseUrl + RedirectUrls.downloadSubmissionFormUrl,
      styleClass: 'parent'
    });
    orderSubMenuItems.push({
      label: TranslationService.translate('vbeiSubmissionForm'),
      icon: 'ei ei-file-check ei-26',
      url: baseUrl + RedirectUrls.uploadSubmissionFormUrl,
      styleClass: 'parent'
    });
    orderSubMenuItems.push({
      label: TranslationService.translate('vbeiViewValidatedOrders'),
      icon: 'ei ei-cart-full ei-26',
      url: baseUrl + RedirectUrls.validatedOrdersUrl,
      styleClass: 'parent'
    });
    this.addManageFavoriteSampleSubMenu(baseUrl, userRights, orderSubMenuItems);
    return orderSubMenuItems;
  }

  addLabContactsMenuItems(baseUrl: string, userRights: UserRights, orderSubMenuItems: MenuItem[]) {
    if (this.configurations.enableContactPage) {
      if (userRights.isCustomerUser) {
        orderSubMenuItems.push({
          label: TranslationService.translate('vbeiLabContacts'),
          icon: 'ei ei-bubble-user ei-26',
          url: baseUrl + RedirectUrls.viewLabContactUrl,
          styleClass: 'parent'
        });
      } else {
        if (userRights.canManageLabContacts) {
          const labContactsSubMenuItems = this.getLabContactsSubMenuItems(baseUrl);
          orderSubMenuItems.push({
            label: TranslationService.translate('vbeiLabContacts'),
            icon: 'ei ei-bubble-user ei-26',
            items: labContactsSubMenuItems,
            styleClass: 'parent'
          });
        }
      }
    }
  }

  addManageFavoriteSampleSubMenu(baseUrl: string, userRights: UserRights, orderSubMenuItems: MenuItem[]) {
    if (this.featureFlags.enableFavouriteSampleManagement &&
      (userRights.canAccessOrderingService || userRights.canOnlyPlaceOrderSubmissionForm || userRights.canPlaceOrderSubmissionForm)) {
      orderSubMenuItems.push({
        label: TranslationService.translate('vbeiManageFavouriteSamples'),
        icon: 'ei ei-star ei-26',
        url: baseUrl + RedirectUrls.manageFavouriteSamplesUrl,
        styleClass: 'parent'
      });
    }
  }

  getResultsSubMenuItems(baseUrl: string, userRights: UserRights): MenuItem[] {
    const resultsSubMenuItems: MenuItem[] = [];
    if (userRights.canAccessResults) {
      resultsSubMenuItems.push({
        label: TranslationService.translate('vbeiMyResults'),
        icon: 'ei ei-pulse ei-26',
        url: baseUrl + RedirectUrls.myResultsUrl,
        styleClass: 'parent'
      });
    }
    if (userRights.canAccessResultsConfiguration) {
      resultsSubMenuItems.push({
        label: TranslationService.translate('vbeiResultsViewConfiguration'),
        icon: 'ei ei-cog ei-26',
        url: baseUrl + RedirectUrls.configureResultsViewUrl,
        styleClass: 'intentMenu'
      });
      resultsSubMenuItems.push({
        label: TranslationService.translate('vbeiManageClientAssignments'),
        icon: 'ei ei-list ei-26',
        url: baseUrl + RedirectUrls.assignViewsByClientUrl,
        styleClass: 'intentMenu'
      });
      resultsSubMenuItems.push({
        label: TranslationService.translate('vbeiManageDefaultAssignments'),
        icon: 'ei ei-list ei-26',
        url: baseUrl + RedirectUrls.selectInitialViewUrl,
        styleClass: 'intentMenu'
      });
    }

    if (userRights.canAccessResultsByOrder) {
      resultsSubMenuItems.push({
        label: TranslationService.translate('vbeiResultsByOrder'),
        icon: 'ei ei-calendar-text ei-26',
        url: baseUrl + RedirectUrls.resultsByOrderUrl,
        styleClass: 'parent'
      });
    }

    if (userRights.canAccessReports && !userRights.canAccessSelfReport) {
      // Commenting as it was mentioned not required as part of PBI:2147416
      // resultsSubMenuItems.push({
      //   label: 'Custom reports',
      //   icon: 'ei ei-chart-bars ei-26',
      //   url: baseUrl + RedirectUrls.customReportsUrl,
      //   styleClass: 'parent'
      // });
    } else {
      if (userRights.canAccessSelfReport) {
        resultsSubMenuItems.push({
          label: TranslationService.translate('vbeiMyReports'),
          icon: 'ei ei-library ei-26',
          url: baseUrl + RedirectUrls.myReportsUrl,
          styleClass: 'parent'
        });
        if (userRights.canManageDefaultReports) {
          resultsSubMenuItems.push({
            label: TranslationService.translate('vbeiDefaultReportBuilder'),
            icon: 'ei ei-construction ei-26',
            url: baseUrl + RedirectUrls.defaultReportBuilderUrl,
            styleClass: 'intentMenu'
          });
        }
        resultsSubMenuItems.push({
          label: TranslationService.translate('vbeiReportBuilder'),
          icon: 'ei ei-hammer-wrench ei-26',
          url: baseUrl + RedirectUrls.reportBuilderUrl,
          styleClass: 'intentMenu'
        });
        if (!this.configurations.disableParameterSortings) {
          resultsSubMenuItems.push({
            label: TranslationService.translate('vbeiCustomParameterSortings'),
            icon: 'ei ei-sort-alpha-asc ei-26',
            url: baseUrl + RedirectUrls.parameterSortingUrl,
            styleClass: 'intentMenu'
          });
        }

        // Commenting as it was mentioned not required as part of PBI:2147416
        // if (this.featureFlags.enableTestPsorting && !userRights.isCustomerUser) {
        //   resultsSubMenuItems.push({
        //     label: 'Test P sorting',
        //     icon: 'ei ei-sort-alpha-asc ei-26',
        //     url: baseUrl + this.configurations.configureSortPatternUri,
        //     styleClass: 'intentMenu'
        //   });
        // }

        if (!this.configurations.disableLimitSets && userRights.canAccessLimitSets) {
          resultsSubMenuItems.push({
            label: TranslationService.translate('vbeiManageLimitSets'),
            icon: 'ei ei-cog ei-26',
            url: baseUrl + RedirectUrls.limitSetsUrl,
            styleClass: 'intentMenu'
          });
        }
        resultsSubMenuItems.push({
          label: TranslationService.translate('vbeiMyExports'),
          icon: 'ei ei-folder-download ei-26',
          url: baseUrl + RedirectUrls.myExportsUrl,
          styleClass: 'intentMenu'
        });
        if (this.configurations.enableMyStatistics) {
          resultsSubMenuItems.push({
            label: TranslationService.translate('vbeiMystatistics'),
            icon: 'ei ei-chart-growth ei-26',
            url: baseUrl + RedirectUrls.myStatisticsUrl,
            styleClass: 'intentMenu'
          });
        }

        // Commenting as it was mentioned not required as part of PBI:2147416
        // if (this.configurations.enableFloorplans) {
        //   resultsSubMenuItems.push({
        //     label: 'Floorplans',
        //     icon: 'ei ei-map-marker ei-26',
        //     url: baseUrl + RedirectUrls.floorPlanUrl,
        //     styleClass: 'parent'
        //   });
        // }
      }
      if (userRights.canAccessSelfReport && userRights.canAccessSelfGraph) {
        resultsSubMenuItems.push({
          label: TranslationService.translate('vbeiStandardGraphs'),
          icon: 'ei ei-graph ei-26',
          url: baseUrl + RedirectUrls.standardGraphsUrl,
          styleClass: 'parent'
        });
      }
      if (this.configurations.enableSampleStatusTracking) {
        resultsSubMenuItems.push({
          label: TranslationService.translate('vbeiSampleStatusTracking'),
          icon: 'ei ei-chart-bars ei-26',
          url: baseUrl + RedirectUrls.sampleStatusTracking,
          styleClass: 'parent'
        });
      }
      if (!userRights.isCustomerUser) {
        resultsSubMenuItems.push({
          label: TranslationService.translate('vbeiMonitoringTool'),
          icon: 'ei ei-screen ei-26',
          url: baseUrl + RedirectUrls.monitoringToolUrl,
          styleClass: 'parent'
        });
      }

      // Commenting as it was mentioned not required as part of PBI:2147416
      // if (userRights.canAccessReports) {
      //   resultsSubMenuItems.push({
      //     label: 'Custom reports',
      //     icon: 'ei ei-chart-bars ei-26',
      //     url: baseUrl + RedirectUrls.customReportsUrl,
      //     styleClass: 'parent'
      //   });
      // }
    }


    return resultsSubMenuItems;
  }

  getNewResultsSubMenuItems(baseUrl: string): MenuItem[] {
    const newResultsMenuItems: MenuItem[] = [];

    newResultsMenuItems.push({
      label: TranslationService.translate('vbeiMyResults'),
      icon: 'ei ei-clipboard-text ei-26',
      url: baseUrl,
      styleClass: 'parent'
    });
    newResultsMenuItems.push({
      label: TranslationService.translate('vbeiManageResultViews'),
      icon: 'ei ei-library ei-26',
      url: baseUrl + RedirectUrls.manageResultsViewUrl,
      styleClass: 'parent'
    });
    newResultsMenuItems.push({
      label: TranslationService.translate('vbeiBuildNewResultView'),
      icon: 'ei ei-plus ei-26',
      url: baseUrl + RedirectUrls.buildNewResultViewUrl,
      styleClass: 'parent'
    });

    return newResultsMenuItems;
  }

  getDocumentsSubMenuItems(baseUrl: string, userRights: UserRights): MenuItem[] {
    const documentsMenuItems: MenuItem[] = [];
    if (userRights.canAccessDocuments) {
      documentsMenuItems.push({
        label: TranslationService.translate('vbeiSharedDocuments'),
        icon: 'ei ei-folder ei-26',
        url: baseUrl + RedirectUrls.sharedDocumentsUrl,
        styleClass: 'parent'
      });
    }

    // Commenting as it was mentioned not required as part of PBI:2147416
    // if (userRights.canViewInvoices && !this.configurations.disableInvoices) {
    //   documentsMenuItems.push({
    //     label: 'Invoices',
    //     icon: 'ei ei-receipt ei-26',
    //     url: baseUrl + RedirectUrls.invoicesUrl,
    //     styleClass: 'parent'
    //   });
    // }
    return documentsMenuItems;
  }

  getAccountSubMenuItems(baseUrl: string, userRights: UserRights): MenuItem[] {
    const accountSubMenuItems: MenuItem[] = [];
    if (userRights.canCreateUser || userRights.canCreateAdmin) {
      accountSubMenuItems.push({
        label: TranslationService.translate('vbeiCreateNewUser'),
        icon: 'ei ei-user-plus ei-26',
        url: baseUrl + RedirectUrls.createNewUserUrl,
        styleClass: 'parent'
      });
    }
    if (userRights.canManageUsers || userRights.canManageAdmins) {
      accountSubMenuItems.push({
        label: TranslationService.translate('vbeiManageUsers'),
        icon: 'ei ei-users2 ei-26',
        url: baseUrl + RedirectUrls.manageUsersUrl,
        styleClass: 'parent'
      });
    }

    return accountSubMenuItems;
  }

  getSettingsSubMenuItems(baseUrl: string, userRights: UserRights): MenuItem[] {
    const settingsSubMenuItems: MenuItem[] = [];
    if (userRights.canAccessIntercoSubscription) {
      settingsSubMenuItems.push({
        label: TranslationService.translate('vbeiConfigureIntercompanyResults'),
        icon: 'ei ei-register ei-26',
        url: baseUrl + RedirectUrls.configureIterCompanyResultsUrl,
        styleClass: 'parent'
      });
    }
    if (userRights.canManageNewsFeed) {
      settingsSubMenuItems.push({
        label: TranslationService.translate('vbeiManageNewsFeed'),
        icon: 'ei ei-papers ei-26',
        url: baseUrl + RedirectUrls.manageNewsFeedUrl,
        styleClass: 'parent'
      });
      settingsSubMenuItems.push({
        label: TranslationService.translate('vbeiCreateNewsFeed'),
        icon: 'ei ei-typewriter ei-26',
        url: baseUrl + RedirectUrls.createNewPostUrl,
        styleClass: 'parent'
      });
    }
    if (userRights.canConfigureEOL) {
      settingsSubMenuItems.push({
        label: TranslationService.translate('vbeiConfigureEOL'),
        icon: 'ei ei-wrench ei-26',
        url: baseUrl + RedirectUrls.configureEOLUrl,
        styleClass: 'parent'
      });
    }
    if (userRights.canEditSurveyExclusionList) {
      settingsSubMenuItems.push({
        label: TranslationService.translate('vbeiClientsExcludedFromSatisfactionSurvey'),
        icon: 'ei ei-users-minus ei-26',
        url: baseUrl + RedirectUrls.exclusionListUrl,
        styleClass: 'parent'
      });
    }
    return settingsSubMenuItems;
  }

  getAlertsSubMenuItems(baseUrl: string): MenuItem[] {
    const alertSubMenuItems: MenuItem[] = [];

    alertSubMenuItems.push({
      label: TranslationService.translate('vbeiAlertHistory'),
      icon: 'ei ei-alarm-snooze ei-26',
      url: baseUrl + RedirectUrls.alertsHistoryUrl,
      styleClass: 'parent'
    });
    alertSubMenuItems.push({
      label: TranslationService.translate('vbeiMyAlerts'),
      icon: 'ei ei-alarm-ringing ei-26',
      url: baseUrl + RedirectUrls.myAlertsUrl,
      styleClass: 'parent'
    });

    return alertSubMenuItems;
  }

  getAssessmentSubMenuItems(baseUrl: string): MenuItem[] {
    const assessmentSubMenuItems: MenuItem[] = [];

    assessmentSubMenuItems.push({
      label: TranslationService.translate('vbeiConfigureAssessments'),
      icon: 'ei ei-chart-settings ei-26',
      url: baseUrl + RedirectUrls.configureAssessmentUrl,
      styleClass: 'parent'
    });
    assessmentSubMenuItems.push({
      label: TranslationService.translate('vbeiReassessmentLogs'),
      icon: 'ei ei-database-refresh ei-26',
      url: baseUrl + RedirectUrls.reassessmentlogsUrl,
      styleClass: 'parent'
    });
    assessmentSubMenuItems.push({
      label: TranslationService.translate('vbeiPartialParameter'),
      icon: 'ei ei-register ei-26',
      url: baseUrl + RedirectUrls.partialParameters,
      styleClass: 'parent'
    });

    return assessmentSubMenuItems;
  }

  getLabContactsSubMenuItems(baseUrl: string): MenuItem[] {
    const labContactsSubMenuItems: MenuItem[] = [];

    labContactsSubMenuItems.push({
      label: TranslationService.translate('vbeiManageLabContacts'),
      icon: 'ei ei-users2 ei-26',
      url: baseUrl + RedirectUrls.manageLabContacts,
      styleClass: 'parent'
    });
    labContactsSubMenuItems.push({
      label: TranslationService.translate('vbeiUploadLabContacts'),
      icon: 'ei ei-users-plus ei-26',
      url: baseUrl + RedirectUrls.uploadLabContactsUrl,
      styleClass: 'parent'
    });

    return labContactsSubMenuItems;
  }

  getUserLoginSubMenuItems(baseUrl: string): MenuItem[] {
    const userSubMenuItems: MenuItem[] = [];
    const url = this.configurations.eIMASLogonURL ?? '';
    const changePasswordUrl: string = url.concat('/myaccount/changepassword?client_id=')
      .concat(this.configurations.eIMASClientApplicationID);
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiYourProfile'),
      icon: 'ei ei-profile ei-26',
      url: baseUrl + RedirectUrls.yourProfileUrl,
      styleClass: 'parent'
    });
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiEditYourProfile'),
      icon: 'ei ei-pencil ei-26',
      url: baseUrl + RedirectUrls.editProfileUrl,
      styleClass: 'parent'
    });
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiChangePassword'),
      icon: 'ei ei-user-lock ei-26',
      url: changePasswordUrl, // RedirectUrls.changePasswordUrl
      styleClass: 'parent'
    });
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiCustomizeMenu'),
      icon: 'ei ei-menu ei-26',
      url: baseUrl + RedirectUrls.customizeMenuUrl,
      styleClass: 'parent'
    });
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiDisclaimer'),
      icon: 'ei ei-clipboard-pencil ei-26',
      url: baseUrl + RedirectUrls.termsOfUseUrl,
      styleClass: 'parent'
    });
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiPrivacyPolicy'),
      icon: 'ei ei-file-preview ei-26',
      url: baseUrl + RedirectUrls.privacyPolicy,
      styleClass: 'parent'
    });
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiCookiePolicy'),
      icon: 'ei ei-eye-crossed ei-26',
      url: baseUrl + RedirectUrls.cookiePolicy,
      styleClass: 'parent'
    });
    userSubMenuItems.push({
      label: TranslationService.translate('vbeiLogout'),
      icon: 'ei ei-exit ei-26',
      command: () => this.logout(baseUrl),
      styleClass: 'parent'
    });
    return userSubMenuItems;
  }

  setupSessionValidator() {
    interval(this.authConfigService.fullConfigurations.configuration.sessionCheckIntervall * 1000)
    .subscribe(() => {
        const sessionId = this.getSessionId();
        if (sessionId !== null) {
            const queryApiBaseUrl = this.appConfigService.configuration?.apiConfig.orderQueryBaseUrl;
            const url = queryApiBaseUrl + 'authentication/session/' + sessionId + '/status';
            this.httpClient.get(url).subscribe((data: any) => {
                if(data !== null && data.isRevoked) {
                    this.oauthService.logOut();
                    window.location.reload();
                }
            });
        }
    });
}

  getSessionId(): string {
    const idClaims = this.oauthService.getIdentityClaims();
    type ObjectKey = keyof typeof idClaims;
    if (idClaims === null || idClaims === undefined) {
      return null;
    }
    return idClaims['pi.sri' as ObjectKey];
  }

  logout(baseUrl: string = null): void {
    this.logOut();
  }

  logOut() {
    const httpOptions = {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        })
      };
    const sessionId = this.getSessionId();
    if (sessionId !== null) {
        const queryApiBaseUrl = this.appConfigService.configuration?.apiConfig.orderQueryBaseUrl;
        const url = queryApiBaseUrl + 'authentication/logout/' + sessionId;
        this.httpClient.post(url, null, httpOptions).subscribe(() => {
          this.oauthService.logOut();
          window.location.reload();
        });
    }
}
}
